/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Stack,
  StackProps,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { KeyboardEvent, MutableRefObject, useEffect, useRef, useState } from 'react'
import { AiOutlineInfoCircle, AiOutlineSearch } from 'react-icons/ai'

import XIcon from '../../../assets/icons/xicon.svg'
import { Pagination } from '../../../components/data/Pagination'
import { ProductCard } from '../../../components/data/ProductCard'
import { ReactTable } from '../../../components/data/ReactTable'
import { AccessModal } from '../../../components/widgets/AccessModal'
import { apiSensedia } from '../../../services/api-sensedia'
import { randomComponentId } from '../../../utils/components/randomComponentId'

type Broker = {
  id: string
  created_at: string
  updated_at: string
  codigo_produtor: number
  codigo_estrutura_venda: number
  nome_assessoria: string
  nome_corretora: string
  situacao: string
  vendas_mes: number
  premio_emitido_mes_saude: number
  premio_emitido_mes_odonto: number
  premio_emitido_mes_vida: number
  premio_emitido_mes_previdencia: number
  representatividade_total?: number
}

type ExpandedComponentProps = {
  row?: any
} & StackProps

export const Brokers = ({ totalFaturamentoMes }: { totalFaturamentoMes: number }) => {
  const toast = useToast()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [isLoading, setIsLoading] = useState(false)

  const [BrokersAmount, setBrokersAmount] = useState(0)

  const nomeInputRef = useRef() as MutableRefObject<HTMLInputElement>
  const situationRef = useRef() as MutableRefObject<HTMLSelectElement>
  const [limitValue, setLimitValue] = useState('10')
  const [orderByValue, setOrderByValue] = useState('vendas_mes')

  const [currentPage, setCurrentPage] = useState(1)

  const [brokers, setBrokers] = useState<Broker[]>([])
  const columnHelper = createColumnHelper<Broker>()

  const columns = [
    columnHelper.accessor('nome_corretora', {
      cell: info => info.getValue(),
      header: () => <Text display="inline">Nome</Text>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor('situacao', {
      cell: info => (info.getValue() === 'PRODUTIVO' ? 'Produtivo' : 'Improdutivo'),
      header: () => (
        <HStack>
          <Text display="inline">Situação</Text>{' '}
          <Tooltip
            label={
              <Stack>
                <Text>
                  <b>Produtivo:</b> corretores/corretoras que possuem vendas no ano anterior
                </Text>
                <Text>
                  <b>Improdutivo:</b> corretores/corretoras que podem ter estoque ou não, mas não possuem vendas no ano
                  anterior
                </Text>
              </Stack>
            }
            shouldWrapChildren
          >
            <AiOutlineInfoCircle size={20} />
          </Tooltip>
        </HStack>
      ),

      aggregatedCell: () => <h1>teste</h1>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor('vendas_mes', {
      cell: info => (
        <Text color="#006217" fontWeight="bold">
          {info.getValue<number>().toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          })}
        </Text>
      ),
      header: () => <Text display="inline">Vendas no mês</Text>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor('representatividade_total', {
      cell: info => (
        <Text color="#146CFA" fontWeight="bold">
          {info.row.getCanExpand() && (
            <Button
              cursor="pointer"
              _hover={{
                background: 'transparent',
              }}
              variant="ghost"
              onClick={info.row.getToggleExpandedHandler()}
            >
              {info.row.getIsExpanded() ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Button>
          )}
          {info.getValue().toFixed(2)}%
        </Text>
      ),
      header: () => <Text display="inline">Rep. na produção total</Text>,
      footer: info => info.column.id,
    }),
    columnHelper.display({
      id: 'details',
      cell: info =>
        info.row.getIsExpanded() ? (
          <Box title="Detalhes">
            <ChevronUpIcon onClick={() => info.row.toggleExpanded()} cursor="pointer" />
          </Box>
        ) : (
          <Box title="Detalhes">
            <ChevronDownIcon title="Detalhes" cursor="pointer" onClick={() => info.row.toggleExpanded()} />
          </Box>
        ),
    }),
  ]

  const getData = async ({
    nome_corretora = nomeInputRef.current.value || undefined,
    situacao = situationRef.current.value || undefined,
    page = currentPage,
    limit = limitValue,
    order_by = orderByValue,
    order = 'desc',
  }) => {
    try {
      setIsLoading(true)

      const params = {
        nome_corretora,
        situacao: situacao,
        page: page - 1,
        limit,
        order_by: situacao ? null : order_by,
        order,
      }

      const { data: response } = await apiSensedia.get('/assessorias/corretoras', {
        params,
      })

      const brokers: Broker[] = response.data.map((broker: Broker) => {
        return { ...broker, representatividade_total: (broker.vendas_mes / totalFaturamentoMes) * 100 }
      })

      setBrokersAmount(response.count)

      setIsLoading(false)
      setBrokers(brokers)
      setCurrentPage(page)
    } catch (error: any) {
      console.log(error)

      if (error?.response?.status === 401) {
        onOpen()

        setIsLoading(false)

        return
      }

      toast({
        position: 'top',
        description: error.response?.data,
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    const nome_corretora = nomeInputRef.current.value

    if (e.key === 'Enter') {
      if (nome_corretora.length === 0) {
        toast({
          position: 'top',
          description: 'Favor inserir pelo menos uma letra',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })

        return
      }

      getData({ order_by: 'nome_corretora', nome_corretora, page: 1 })
    }
  }

  const handleNameFilter = () => {
    const nome_corretora = nomeInputRef.current.value

    if (nome_corretora.length === 0) {
      toast({
        position: 'top',
        description: 'Favor inserir pelo menos uma letra',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })

      return
    }

    situationRef.current.value = ''

    getData({ order_by: 'nome_corretora', nome_corretora, page: 1 })
  }

  const handleClearNameFilter = () => {
    nomeInputRef.current.value = ''
    situationRef.current.value = ''

    getData({ situacao: undefined, page: 1 })
  }

  const handleSituationFilter = () => {
    nomeInputRef.current.value = ''

    getData({ page: 1 })
  }

  useEffect(() => {
    if (totalFaturamentoMes > 0) {
      getData({})
    }
  }, [totalFaturamentoMes])

  const ExpandedComponent = ({ row, ...rest }: ExpandedComponentProps) => {
    const calculatePercentage = (partial: number, total: number) => {
      return Number(((partial / total) * 100).toFixed(2))
    }

    return (
      <HStack {...rest} w="100%" justify="space-around" gap="2rem">
        <ProductCard
          info={
            <Stack>
              <Text>Estes valores compreendem a(s) carteira(s):</Text>
              <Text>• Previdência Individual</Text>
            </Stack>
          }
          title={'Previdência'}
          value={row.original.premio_emitido_mes_previdencia}
          rep={calculatePercentage(row.original.premio_emitido_mes_previdencia, row.original.vendas_mes)}
        />
        <ProductCard
          info={
            <Stack>
              <Text>Estes valores compreendem a(s) carteira(s):</Text>
              <Text>• Vida Individual</Text>
              <Text>• Vida Coletivo</Text>
            </Stack>
          }
          title={'Vida'}
          value={row.original.premio_emitido_mes_vida}
          rep={calculatePercentage(row.original.premio_emitido_mes_vida, row.original.vendas_mes)}
        />
        <ProductCard
          info={
            <Stack>
              <Text>Estes valores compreendem a(s) carteira(s):</Text>

              <Text>• Saúde PME</Text>
              <Text>• Saúde Empresarial</Text>
            </Stack>
          }
          title={'Saúde'}
          value={row.original.premio_emitido_mes_saude}
          rep={calculatePercentage(row.original.premio_emitido_mes_saude, row.original.vendas_mes)}
        />
        <ProductCard
          info={
            <Stack>
              <Text>Estes valores compreendem a(s) carteira(s):</Text>

              <Text>• Odonto Individual</Text>
              <Text>• Odonto Empresarial</Text>
              <Text>• Odonto PME</Text>
            </Stack>
          }
          title={'Odonto'}
          value={row.original.premio_emitido_mes_odonto}
          rep={calculatePercentage(row.original.premio_emitido_mes_odonto, row.original.vendas_mes)}
        />
      </HStack>
    )
  }

  return (
    <Flex w="100%" maxW="79vw" justify="center" align="center">
      <Box
        boxShadow="0px 0px 5px rgba(50, 50, 50, 0.25)"
        borderRadius="24px"
        width="100%"
        pointerEvents={isLoading ? 'none' : 'auto'}
        opacity={isLoading ? '.4' : '1'}
        p="20px"
        color="#001E64"
        bg="#fff"
      >
        <HStack w="100%" justify="space-between" flexDir={{ base: 'column', md: 'row' }} gap="1rem">
          <Stack>
            <HStack align="center" justify="center">
              <Heading fontSize="1.4rem">Acompanhamento parceiros</Heading>
              <Tooltip label="Acompanhe os corretores abaixo da sua assessoria." shouldWrapChildren>
                <AiOutlineInfoCircle size={20} />
              </Tooltip>
            </HStack>
            <Text>
              Quantidade de parceiros: <b>{BrokersAmount}</b>
            </Text>
          </Stack>
          <HStack flexDir={{ base: 'column', md: 'row' }} gap="1rem">
            <Select
              placeholder="Situação"
              ref={situationRef}
              borderRadius="24px"
              onChange={handleSituationFilter}
              border="1px solid #001E64 !important"
              w={{ base: '100%', sm: '13rem', md: '13rem' }}
            >
              <option value="PRODUTIVO">Produtivo</option>
              <option value="NÃO PRODUTIVO">Improdutivo</option>
            </Select>
            <InputGroup display="flex" align="center" justify="center">
              <InputRightElement
                right="3rem"
                top=".25rem"
                bg="#F4F4F4"
                w="2rem"
                h="2rem"
                onClick={() => handleClearNameFilter()}
                borderRadius="50%"
                cursor="pointer"
              >
                <Image src={XIcon} />
              </InputRightElement>

              <InputRightElement
                borderRadius="24px"
                w="2.5rem"
                borderRightRadius="0"
                cursor="pointer"
                borderLeft="1px solid #001E64 !important"
                borderTop="1px solid #001E64 !important"
                borderBottom="1px solid #001E64 !important"
                bg="#FF5000"
                onClick={() => handleNameFilter()}
                transform="scaleX(-1)"
              >
                <AiOutlineSearch size={20} color="white" />
              </InputRightElement>

              <Input
                ref={nomeInputRef}
                borderRadius="24px"
                onKeyPress={e => handleKeyPress(e)}
                border="1px solid #001E64 !important"
                placeholder="Digite aqui nome/corretora"
                _placeholder={{ color: '#001E64', opacity: '.5' }}
              />
            </InputGroup>
          </HStack>
        </HStack>

        {brokers ? (
          <ReactTable
            columns={columns}
            isLoading={isLoading}
            fnc={getData}
            setOrderBy={setOrderByValue}
            ExpandedComponent={ExpandedComponent}
            data={brokers}
          />
        ) : null}

        <HStack justify="space-between" flexDir={{ base: 'column', md: 'row' }} gap={4}>
          <HStack>
            <Text>Linhas por página</Text>
            <Select
              value={limitValue}
              onChange={e => (getData({ limit: e.target.value }), setLimitValue(e.currentTarget.value))}
              w="7rem"
              border="1px dashed #F4F4F4 !important"
              borderRadius="6px"
            >
              {[10, 20, 30, 50, 100].map(pageSize => (
                <option key={pageSize + randomComponentId()} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Select>
          </HStack>
          <HStack spacing={2} justify="center" maxW="100%">
            <Pagination
              fnc={getData}
              currentPage={currentPage}
              lastPage={Math.ceil(BrokersAmount / +limitValue)}
              maxLength={5}
              setCurrentPage={setCurrentPage}
            />
          </HStack>
        </HStack>
      </Box>
      <AccessModal isOpen={isOpen} onClose={onClose} />
    </Flex>
  )
}
